import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStockSnapshots } from "../marketData/marketDataApi";
import "./NinaDayScanner.css";

const REFRESH_MS = 60000;
const REFRESH_DEADLINE_MS = 30000;
const EXTENDED_FEED = "delayed_sip";
const DEFAULT_UNIVERSE = [
  "PLUG", "AAPL", "NVDA", "AMD", "TSLA", "AMZN", "MSFT", "META",
  "GOOGL", "NFLX", "AVGO", "INTC", "SOFI", "HOOD", "COIN", "SMCI",
  "ARM", "PLTR", "RIVN", "LCID", "F", "NIO", "BAC", "JPM", "XOM",
  "CVX", "UBER", "SPY", "QQQ", "IWM", "SLV", "RXT",
];

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function firstFinite(...values) {
  for (const value of values) {
    const normalized = finiteNumber(value);
    if (normalized !== null) return normalized;
  }
  return null;
}

function formatPrice(value) {
  const number = finiteNumber(value);
  return number === null ? "—" : `$${number.toFixed(2)}`;
}

function formatPercent(value) {
  const number = finiteNumber(value);
  if (number === null) return "—";
  return `${number >= 0 ? "+" : ""}${number.toFixed(2)}%`;
}

function formatVolume(value) {
  const number = finiteNumber(value);
  if (number === null) return "—";
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(number);
}

function formatTimestamp(value) {
  if (!value) return "Update unavailable";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Update unavailable";
  return parsed.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  });
}

function easternClockParts(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(parsed);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const hour = Number(values.hour);
    const minute = Number(values.minute);
    if (!Number.isInteger(hour) || !Number.isInteger(minute) || !values.weekday) return null;
    return { weekday: values.weekday, hour, minute };
  } catch (error) {
    return null;
  }
}

function isAfterHoursTimestamp(value) {
  const parts = easternClockParts(value);
  if (!parts || parts.weekday === "Sat" || parts.weekday === "Sun") return false;
  const minutes = parts.hour * 60 + parts.minute;
  return minutes >= 16 * 60 && minutes < 20 * 60;
}

function timestampMillis(value) {
  if (!value) return null;
  const parsed = new Date(value);
  const millis = parsed.getTime();
  return Number.isNaN(millis) ? null : millis;
}

function freshestEvidence(candidates) {
  return candidates
    .filter(Boolean)
    .map((candidate) => ({ ...candidate, millis: timestampMillis(candidate.timestamp) }))
    .filter((candidate) => candidate.millis !== null)
    .sort((a, b) => b.millis - a.millis)[0] || null;
}

function currentSelectedSymbol() {
  try {
    return String(localStorage.getItem("aninaSelectedSymbol") || "PLUG")
      .trim()
      .toUpperCase();
  } catch (error) {
    return "PLUG";
  }
}

function scannerUniverse() {
  const selected = currentSelectedSymbol();
  return Array.from(new Set([selected, ...DEFAULT_UNIVERSE]));
}

function normalizeSnapshot(symbol, snapshot) {
  const latestTrade = snapshot?.latest_trade || {};
  const latestQuote = snapshot?.latest_quote || {};
  const minuteBar = snapshot?.minute_bar || {};
  const dailyBar = snapshot?.daily_bar || {};
  const source = snapshot?.source || {};
  const tradePrice = finiteNumber(latestTrade.p);
  const barPrice = finiteNumber(minuteBar.c);
  const rawBid = finiteNumber(latestQuote.bp);
  const rawAsk = finiteNumber(latestQuote.ap);

  const tradeEvidence =
    tradePrice !== null && latestTrade.t
      ? { kind: "trade", timestamp: latestTrade.t, price: tradePrice }
      : null;
  const barEvidence =
    barPrice !== null && minuteBar.t
      ? { kind: "minute_bar", timestamp: minuteBar.t, price: barPrice }
      : null;
  const quoteEvidence =
    (rawBid !== null || rawAsk !== null) && latestQuote.t
      ? { kind: "quote", timestamp: latestQuote.t, price: null }
      : null;

  const freshestAuthenticatedEvidence = freshestEvidence([
    tradeEvidence,
    barEvidence,
    quoteEvidence,
  ]);
  const afterHoursPriceEvidence = freshestEvidence(
    [tradeEvidence, barEvidence].filter(
      (candidate) => candidate && isAfterHoursTimestamp(candidate.timestamp)
    )
  );
  const quoteIsAfterHours = isAfterHoursTimestamp(latestQuote.t);
  const latestPrice = afterHoursPriceEvidence?.price ?? null;
  const regularClose = finiteNumber(dailyBar.c);
  const dollarChange =
    latestPrice !== null && regularClose !== null ? latestPrice - regularClose : null;
  const percentageChange =
    dollarChange !== null && regularClose > 0 ? (dollarChange / regularClose) * 100 : null;
  const bid = quoteIsAfterHours ? rawBid : null;
  const ask = quoteIsAfterHours ? rawAsk : null;
  const spread = bid !== null && ask !== null && ask >= bid ? ask - bid : null;
  const updatedAt = freshestAuthenticatedEvidence?.timestamp || null;

  return {
    symbol,
    latestPrice,
    regularClose,
    dollarChange,
    percentageChange,
    bid,
    ask,
    spread,
    volume: finiteNumber(dailyBar.v),
    vwap: firstFinite(
      isAfterHoursTimestamp(minuteBar.t) ? minuteBar.vw : null,
      dailyBar.vw
    ),
    updatedAt,
    isAfterHours: Boolean(
      freshestAuthenticatedEvidence && isAfterHoursTimestamp(updatedAt)
    ),
    evidenceKind: freshestAuthenticatedEvidence?.kind || "unavailable",
    priceEvidenceKind: afterHoursPriceEvidence?.kind || "unavailable",
    provider: source.provider || "Alpaca",
    feed: source.feed || "",
    quality: source.quality_level || source.quality_state || "Unknown",
    notice: source.nina_data_quality_notice || "",
  };
}

export default function NinaLiveAfterHoursScanner({
  onOpenTradeReview,
  refreshDeadlineMs = REFRESH_DEADLINE_MS,
}) {
  const symbols = useMemo(() => scannerUniverse(), []);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [refreshWarning, setRefreshWarning] = useState("");
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const refreshInFlightRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  const loadSnapshots = useCallback(async ({ forceRefresh = false } = {}) => {
    if (refreshInFlightRef.current) return false;

    refreshInFlightRef.current = true;
    const isRefresh = hasLoadedOnceRef.current;
    const controller = new AbortController();
    let deadlineReached = false;
    const deadlineId = window.setTimeout(() => {
      deadlineReached = true;
      controller.abort();
    }, refreshDeadlineMs);

    setRefreshWarning("");
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setStatus("loading");
    }

    try {
      const payload = await getStockSnapshots(symbols, EXTENDED_FEED, {
        signal: controller.signal,
        ...(forceRefresh ? { cacheMode: "reload" } : {}),
      });
      if (deadlineReached || controller.signal.aborted) {
        setRefreshWarning(
          "Authenticated after-hours refresh timed out. Previous verified rows were kept; try Refresh now again."
        );
        if (!isRefresh) setStatus("empty");
        return false;
      }

      const snapshots = payload?.snapshots || {};
      const missingSymbols = new Set(payload?.missing_symbols || []);
      const authenticatedRows = [];
      const nextErrors = [];

      symbols.forEach((symbol) => {
        const snapshot = snapshots[symbol];
        if (!snapshot) {
          nextErrors.push({
            symbol,
            message: missingSymbols.has(symbol)
              ? "No authenticated Alpaca snapshot was returned."
              : "Authenticated Alpaca market data is unavailable.",
          });
          return;
        }

        const normalized = normalizeSnapshot(symbol, snapshot);
        if (normalized.isAfterHours) authenticatedRows.push(normalized);
      });

      authenticatedRows.sort((a, b) => {
        const aMove = Math.abs(finiteNumber(a.percentageChange) ?? -Infinity);
        const bMove = Math.abs(finiteNumber(b.percentageChange) ?? -Infinity);
        return bMove - aMove || a.symbol.localeCompare(b.symbol);
      });

      setRows(authenticatedRows);
      setErrors(nextErrors);
      setLastRefreshAt(new Date());
      setStatus(authenticatedRows.length ? "ready" : "empty");
      hasLoadedOnceRef.current = true;
      return true;
    } catch (error) {
      setRefreshWarning(
        controller.signal.aborted
          ? "Authenticated after-hours refresh timed out. Previous verified rows were kept; try Refresh now again."
          : "Authenticated after-hours refresh failed. Previous verified rows were kept; try Refresh now again."
      );
      if (!isRefresh) setStatus("empty");
      return false;
    } finally {
      window.clearTimeout(deadlineId);
      refreshInFlightRef.current = false;
      setIsRefreshing(false);
    }
  }, [refreshDeadlineMs, symbols]);

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (active) await loadSnapshots();
    };
    run();
    const timerId = window.setInterval(run, REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(timerId);
    };
  }, [loadSnapshots]);

  const openSymbol = (row) => {
    try {
      localStorage.setItem("aninaSelectedSymbol", row.symbol);
    } catch (error) {
      // Current-session navigation continues through the events below.
    }

    window.dispatchEvent(
      new CustomEvent("nina:symbol-change", { detail: { symbol: row.symbol } })
    );

    if (typeof onOpenTradeReview === "function") {
      onOpenTradeReview({
        kind: "after-hours-live-stock",
        symbol: row.symbol,
        source: "alpaca-live-after-hours-scanner",
        evidenceStatus: "provider-live",
        currentPrice: row.latestPrice,
        regularClose: row.regularClose,
        provider: row.provider,
        feed: row.feed,
        dataQuality: row.quality,
        reviewOnly: true,
      });
    }
  };

  return (
    <section className="ninaScannerPage" aria-labelledby="live-after-hours-scanner-title">
      <header className="scannerHero scannerCompactPanel">
        <span className="scannerEyebrow">AUTHENTICATED EXTENDED-HOURS DATA · 15-MIN DELAY</span>
        <h1 id="live-after-hours-scanner-title">🌙 AI After-Hours Scanner</h1>
        <p>
          Surface authenticated post-market movers only when the freshest delayed-SIP trade,
          minute-bar, or quote evidence falls between 4:00 PM and 8:00 PM New York time.
        </p>
        <div className="scannerDemoNotice" role="note">
          <strong>Regular close and after-hours price stay separate</strong>
          <span>
            Movement is measured only when an authenticated after-hours trade or minute bar supplies
            a price. Quote-only after-hours evidence may appear with bid/ask while Last and Change stay
            unavailable. Extended-hours evidence uses consolidated SIP data delayed by about 15 minutes.
          </span>
        </div>
      </header>

      <section className="scannerCompactPanel" aria-label="After-hours scanner data status">
        <div className="scannerStatusRow">
          <strong>
            {status === "loading"
              ? "Loading authenticated after-hours snapshots…"
              : isRefreshing
              ? "Refreshing authenticated after-hours snapshots…"
              : status === "ready"
              ? `${rows.length} authenticated after-hours symbols loaded`
              : "No authenticated after-hours rows in the current snapshot set"}
          </strong>
          <span>
            {lastRefreshAt
              ? `Last refresh ${formatTimestamp(lastRefreshAt.toISOString())}`
              : "Waiting for first refresh"}
          </span>
          <button
            type="button"
            onClick={() => loadSnapshots({ forceRefresh: true })}
            disabled={status === "loading" || isRefreshing}
          >
            {isRefreshing ? "Refreshing…" : "Refresh now"}
          </button>
        </div>

        {refreshWarning && (
          <div className="scannerDemoNotice" role="alert">
            <strong>Refresh incomplete</strong>
            <span>{refreshWarning}</span>
          </div>
        )}

        {errors.length > 0 && (
          <div className="scannerDemoNotice" role="status">
            <strong>Partial provider coverage</strong>
            <span>{errors.map((error) => `${error.symbol}: ${error.message}`).join(" • ")}</span>
          </div>
        )}
      </section>

      <section className="scannerCompactPanel">
        <div className="scannerTableWrap">
          <table className="scannerTable">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>After-Hours Last</th>
                <th>Regular Close</th>
                <th>AH Change</th>
                <th>Bid</th>
                <th>Ask</th>
                <th>Spread</th>
                <th>Day Volume</th>
                <th>VWAP</th>
                <th>Provider / Feed</th>
                <th>Quality</th>
                <th>Updated ET</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.symbol}>
                  <td>
                    <button
                      type="button"
                      className="scannerSymbolButton"
                      onClick={() => openSymbol(row)}
                      title={`Open ${row.symbol} in Live Trading`}
                    >
                      {row.symbol}
                    </button>
                  </td>
                  <td aria-label={row.latestPrice === null ? "After-hours last unavailable" : undefined}>
                    {formatPrice(row.latestPrice)}
                  </td>
                  <td>{formatPrice(row.regularClose)}</td>
                  <td
                    aria-label={row.percentageChange === null ? "After-hours change unavailable" : undefined}
                    className={
                      finiteNumber(row.percentageChange) === null
                        ? ""
                        : row.percentageChange < 0
                        ? "negative"
                        : "positive"
                    }
                  >
                    {formatPercent(row.percentageChange)}
                  </td>
                  <td>{formatPrice(row.bid)}</td>
                  <td>{formatPrice(row.ask)}</td>
                  <td>{formatPrice(row.spread)}</td>
                  <td>{formatVolume(row.volume)}</td>
                  <td>{formatPrice(row.vwap)}</td>
                  <td>{row.provider} {row.feed ? `· ${row.feed.toUpperCase()}` : ""}</td>
                  <td>{row.quality}</td>
                  <td>{formatTimestamp(row.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {status === "empty" && !isRefreshing && (
          <div className="scannerDemoNotice" role="status">
            <strong>No after-hours rows passed the timestamp check.</strong>
            <span>
              AninaTrade leaves this LIVE scanner empty rather than recycling regular-session,
              premarket, or simulated prices as after-hours evidence.
            </span>
          </div>
        )}
      </section>
    </section>
  );
}
